import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';

export enum PnlReportTableConfigPage {
  REPORTS = 'REPORTS',
}

export interface PnlReportColumnConfigItem {
  label: string;
  key: string;
  order: number;
  visible?: boolean;
}

export interface PnlReportTableConfigPayload {
  name?: string;
  fieldConfigJson?: {
    data: PnlReportColumnConfigItem[];
  };
  isDefault?: boolean;
}

export interface PnlReportTableConfig {
  id?: number;
  name?: string;
  configName?: string;
  fieldConfigJson?: {
    data: PnlReportColumnConfigItem[];
  };
  data?: PnlReportColumnConfigItem[];
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdByUser?: {
    ID: number;
    FullName: string;
    EmailID: string;
  };
}

export interface PnlReportQueryParams {
  page: number;
  pageSize: number;
  search?: string;
  companyCode?: string;
  glCode?: string;
  glAccount?: string;
  costCenter?: string;
  department?: string;
  segment?: string;
  financialYear?: number;
  month?: string;
  historyYears?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc' | 'ASC' | 'DESC';
}

export interface PnlReportDropdownOption {
  label: string;
  value: string;
}

export interface PnlReportDropdownParams {
  companyCode?: string;
  segment?: string;
  department?: string;
}

export interface PnlReportDropdownResponse {
  data: PnlReportDropdownOption[];
}

export interface PnlReportMeta {
  total: number;
  page: number;
  limit?: number;
  pageSize?: number;
  totalPages: number;
  financialYear?: number;
  periodMonth?: string;
  periodColumn?: string;
  historyYears?: number;
  years?: number[];
  calculationColumns?: PnlReportColumnConfigItem[];
  aliasColumns?: PnlReportColumnConfigItem[];
  sourceColumns?: PnlReportColumnConfigItem[];
}

export interface PnlReportResponse {
  data: Record<string, any>[];
  meta: PnlReportMeta;
  message?: string;
}

export type PnlDashboardMeasure =
  | 'currentYtd'
  | 'previousYtd'
  | 'currentPeriod'
  | 'previousCurrentPeriod';

export type PnlDashboardDimension =
  | 'state'
  | 'department'
  | 'glCode'
  | 'segment'
  | 'stateSegment';

export interface PnlDashboardFilters {
  companyCodes?: string[];
  states?: string[];
  departments?: string[];
  segments?: string[];
  glCodes?: string[];
  search?: string;
}

export interface PnlDashboardRequest extends PnlDashboardFilters {
  financialYear: number;
  month: string;
  measures: PnlDashboardMeasure[];
  dimensions?: PnlDashboardDimension[];
  limit?: number;
}

export interface PnlDashboardDetailsRequest extends PnlDashboardRequest {
  dimension: PnlDashboardDimension;
  category: string;
  secondaryCategory?: string;
  page?: number;
  pageSize?: number;
}

export interface PnlDashboardMetadata {
  measures: { value: PnlDashboardMeasure; label: string }[];
  dimensions: { value: PnlDashboardDimension; label: string }[];
  months: string[];
  filters: {
    companyCodes: string[];
    states: string[];
    departments: string[];
    segments: string[];
    glCodes: string[];
    financialYears: number[];
  };
}

export interface PnlDashboardPoint {
  category: string;
  secondaryCategory: string | null;
  recordCount: number;
  values: Partial<Record<PnlDashboardMeasure, number>>;
}

export interface PnlDashboardChart {
  dimension: PnlDashboardDimension;
  title: string;
  categories: string[];
  secondaryCategories: string[];
  recordCounts: number[];
  points: PnlDashboardPoint[];
  series: {
    key: PnlDashboardMeasure;
    name: string;
    data: number[];
    total: number;
  }[];
}

export interface PnlDashboardResponse {
  context: {
    financialYear: number;
    previousFinancialYear: number;
    month: string;
    measures: PnlDashboardMeasure[];
  };
  totals: Partial<Record<PnlDashboardMeasure, number>>;
  charts: PnlDashboardChart[];
}

export interface PnlDashboardDetailRow {
  companyCode: string | null;
  glCode: string | null;
  costCenter: string | null;
  department: string | null;
  state: string | null;
  segment: string | null;
  group: string | null;
  subgroup: string | null;
  groupText: string | null;
  subgroupText: string | null;
  recordCount: number;
  measures: Partial<Record<PnlDashboardMeasure, number>>;
}

export interface PnlDashboardDetailsResponse {
  data: PnlDashboardDetailRow[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  drilldown: {
    dimension: PnlDashboardDimension;
    category: string;
    secondaryCategory: string | null;
  };
}

const NON_DISPLAY_CALCULATION_KEY_SUFFIXES = [
  '.label',
  '.year',
  '.type',
  '.periodMonth',
  '.periodColumn',
];

const isDisplayablePnlReportColumn = (key: string) =>
  !NON_DISPLAY_CALCULATION_KEY_SUFFIXES.some(suffix => key.endsWith(suffix));

function parsePnlReportFieldConfigJson(
  fieldConfigJson: unknown,
): { data: PnlReportColumnConfigItem[] } | undefined {
  if (fieldConfigJson == null) return undefined;

  let parsed: unknown = fieldConfigJson;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return undefined;
    }
  }

  if (Array.isArray(parsed)) {
    return { data: parsed as PnlReportColumnConfigItem[] };
  }

  if (
    parsed &&
    typeof parsed === 'object' &&
    Array.isArray((parsed as { data?: unknown }).data)
  ) {
    return parsed as { data: PnlReportColumnConfigItem[] };
  }

  return undefined;
}

export function extractPnlReportActiveConfigId(
  raw: unknown,
): number | undefined {
  if (!raw || typeof raw !== 'object') return undefined;

  const record = raw as Record<string, unknown>;
  if (typeof record.configId === 'number') return record.configId;
  if (typeof record.tableConfigId === 'number') return record.tableConfigId;

  if (record.config && typeof record.config === 'object') {
    const config = record.config as Record<string, unknown>;
    if (typeof config.id === 'number') return config.id;
  }

  if (typeof record.id === 'number') return record.id;
  return undefined;
}

export function normalizePnlReportTableConfig(
  raw: unknown,
): PnlReportTableConfig | null {
  if (!raw || typeof raw !== 'object') return null;

  const record = raw as Record<string, unknown>;
  const nested =
    record.config && typeof record.config === 'object'
      ? (record.config as PnlReportTableConfig)
      : (raw as PnlReportTableConfig);

  const fieldConfigJson =
    parsePnlReportFieldConfigJson(nested.fieldConfigJson) ||
    (Array.isArray(nested.data) ? { data: nested.data } : undefined);

  if (!fieldConfigJson?.data?.length) {
    const id = extractPnlReportActiveConfigId(raw);
    if (id) return { ...nested, id };
    return null;
  }

  return {
    ...nested,
    fieldConfigJson,
    data: nested.data ?? fieldConfigJson.data,
  };
}

export function getPnlReportConfigColumns(
  config?: PnlReportTableConfig | null,
): PnlReportColumnConfigItem[] {
  if (!config) return [];

  const fieldConfig =
    parsePnlReportFieldConfigJson(config.fieldConfigJson) ||
    (Array.isArray(config.data) ? { data: config.data } : undefined);

  return [...(fieldConfig?.data ?? [])]
    .filter(
      column =>
        column.visible !== false && isDisplayablePnlReportColumn(column.key),
    )
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

export const pnlReportApi = createApi({
  reducerPath: 'pnlReport',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['PnlReport', 'PnlReportTableConfig'],
  endpoints: builder => ({
    getPnlReport: builder.query<PnlReportResponse, PnlReportQueryParams>({
      query: params => ({
        url: `/contract-management/pnl-report${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['PnlReport'],
    }),

    exportPnlReport: builder.query<Blob, Partial<PnlReportQueryParams>>({
      query: params => ({
        url: `/contract-management/pnl-report/export${parseQueryParams(params || {})}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    getPnlReportCompanyCodeDropdown: builder.query<
      PnlReportDropdownResponse,
      PnlReportDropdownParams | void
    >({
      query: params => ({
        url: `/contract-management/pnl-report/dropdowns/company-codes${parseQueryParams(params || {})}`,
        method: 'GET',
      }),
    }),

    getPnlReportSegmentDropdown: builder.query<
      PnlReportDropdownResponse,
      PnlReportDropdownParams | void
    >({
      query: params => ({
        url: `/contract-management/pnl-report/dropdowns/segments${parseQueryParams(params || {})}`,
        method: 'GET',
      }),
    }),

    getPnlReportDepartmentDropdown: builder.query<
      PnlReportDropdownResponse,
      PnlReportDropdownParams | void
    >({
      query: params => ({
        url: `/contract-management/pnl-report/dropdowns/departments${parseQueryParams(params || {})}`,
        method: 'GET',
      }),
    }),

    getPnlDashboardMetadata: builder.query<
      PnlDashboardMetadata,
      PnlReportDropdownParams | void
    >({
      query: params => ({
        url: `/contract-management/pnl-report/dashboard/metadata${parseQueryParams(params || {})}`,
        method: 'GET',
      }),
    }),

    getPnlDashboardCharts: builder.mutation<
      PnlDashboardResponse,
      PnlDashboardRequest
    >({
      query: body => ({
        url: '/contract-management/pnl-report/dashboard/charts',
        method: 'POST',
        body,
      }),
    }),

    getPnlDashboardDetails: builder.mutation<
      PnlDashboardDetailsResponse,
      PnlDashboardDetailsRequest
    >({
      query: body => ({
        url: '/contract-management/pnl-report/dashboard/details',
        method: 'POST',
        body,
      }),
    }),

    getPnlReportTableConfigList: builder.query<
      BackendResponse<PnlReportTableConfig[]>,
      void
    >({
      query: () => ({
        url: '/contract-management/pnl-report-table-config/list',
        method: 'GET',
      }),
      providesTags: ['PnlReportTableConfig'],
    }),

    getActivePnlReportTableConfig: builder.query<
      BackendResponse<PnlReportTableConfig>,
      { page: PnlReportTableConfigPage }
    >({
      query: params => ({
        url: `/contract-management/pnl-report-table-config/current-config-page${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['PnlReportTableConfig'],
    }),

    getPnlReportTableConfig: builder.query<
      BackendResponse<PnlReportTableConfig>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/contract-management/pnl-report-table-config/${id}`,
        method: 'GET',
      }),
      providesTags: (_, __, { id }) => [{ type: 'PnlReportTableConfig', id }],
    }),

    createPnlReportTableConfig: builder.mutation<
      BackendResponse<PnlReportTableConfig>,
      Required<Pick<PnlReportTableConfigPayload, 'name' | 'fieldConfigJson'>> &
        Pick<PnlReportTableConfigPayload, 'isDefault'>
    >({
      query: payload => ({
        url: '/contract-management/pnl-report-table-config/create-config',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['PnlReportTableConfig'],
    }),

    updatePnlReportTableConfig: builder.mutation<
      BackendResponse<PnlReportTableConfig>,
      { id: number; data: PnlReportTableConfigPayload }
    >({
      query: ({ id, data }) => ({
        url: `/contract-management/pnl-report-table-config/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['PnlReportTableConfig'],
    }),

    deletePnlReportTableConfig: builder.mutation<
      BackendResponse<void>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/contract-management/pnl-report-table-config/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PnlReportTableConfig'],
    }),

    applyPnlReportTableConfig: builder.mutation<
      BackendResponse<void>,
      { configId: number; page: PnlReportTableConfigPage }
    >({
      query: payload => ({
        url: '/contract-management/pnl-report-table-config/apply-config',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['PnlReportTableConfig'],
    }),
  }),
});

export const {
  useGetPnlReportQuery,
  useLazyExportPnlReportQuery,
  useGetPnlReportCompanyCodeDropdownQuery,
  useGetPnlReportSegmentDropdownQuery,
  useGetPnlReportDepartmentDropdownQuery,
  useGetPnlDashboardMetadataQuery,
  useGetPnlDashboardChartsMutation,
  useGetPnlDashboardDetailsMutation,
  useGetPnlReportTableConfigListQuery,
  useGetActivePnlReportTableConfigQuery,
  useGetPnlReportTableConfigQuery,
  useLazyGetPnlReportTableConfigQuery,
  useCreatePnlReportTableConfigMutation,
  useUpdatePnlReportTableConfigMutation,
  useDeletePnlReportTableConfigMutation,
  useApplyPnlReportTableConfigMutation,
} = pnlReportApi;

export default pnlReportApi;

export const useGetPnlReportsQuery = useGetPnlReportQuery;
export const useLazyExportPnlReportsQuery = useLazyExportPnlReportQuery;
export const useLazyGetPnlReportsQuery =
  pnlReportApi.endpoints.getPnlReport.useLazyQuery;
